<template>
<div class="ChecklistStatusDialogCategory">
  <div class="ChecklistStatusDialogCategory__titleContainer text--primary font-weight-medium">
    {{ $t(`common.statusCategoryNames.${category.name}`) }}
  </div>

  <div class="ChecklistStatusDialogCategory__statusContainer">
    <Draggable
      item-key="id"
      :list="category.statuses"
      handle=".ChecklistStatusDialogCategory__grabIcon"
      class="ChecklistStatusDialogCategory__draggableArea"
      group="statuses"
      :forceAutoScrollFallback="true"
      :emptyInsertThreshold="100"
      :delay="500"
      :delayOnTouchOnly="true"
    >
      <template #item="{ element: status }">
        <div
             :key="`${category.name}-status-${status.label}`"
             class="ChecklistStatusDialogCategory__statusFlexContainer"
        >
            <div v-if="editMode && status.isEditable" class="ChecklistStatusDialogCategory__grabIcon pr-2">
              <app-material-icon name="drag_indicator" colo="icon-secondary" />
            </div>

            <div class="status-chip-tick-wrapper d-flex align-center justify-space-between w-100 column-gap-2 py-2 px-1 rounded-lg"
                 :class="{ 'selectable': !editMode && category.areStatusesSelectable }"
                 @click="category.areStatusesSelectable && onStatusClick(status.id)"
            >
              <AppTooltip :disabled="status.label.length <= statusLabelMaxDisplayedLength">
                <template #activator="{ props }">
                  <v-chip
                    class="ChecklistStatusDialogCategory__status font-weight-medium text-white px-2"
                    :color="status.color || statusCategoryColor"
                    :disabled="!category.areStatusesSelectable"
                    variant="flat"
                    v-bind="props"
                  >
                    {{ status.label }}
                  </v-chip>
                </template>
                <span>{{ status.label }}</span>
              </AppTooltip>
              <div v-if="!editMode">
                <app-material-icon
                  name="check"
                  color="icon-info-bold"
                  size="md"
                  :style="{ visibility: (status.id === selectedStatusId) ? 'visible' : 'hidden' }"
                />
              </div>
            </div>

            <div v-if="editMode && status.isEditable" class="ChecklistStatusDialogCategory__edition-buttons ml-2">
              <AppTooltip>
                <template #activator="{ props }">
                  <app-button
                    type="icon"
                    size="small"
                    v-bind="props"
                    @click="() => onStatusDelete(status)"
                  >
                    <app-material-icon name="delete" />
                  </app-button>
                </template>
                <span>{{ $t('project.checklist.dialogs.ChecklistStatusDialogCategory.deleteStatusTooltip') }}</span>
              </AppTooltip>

              <ChecklistStatusDialogEditMenu :status="status"
                                            :category="category.name"
                                            @edit="onStatusEdit"
              />
            </div>
        </div>
      </template>
    </Draggable>
  </div>

  <div v-if="!editMode && currentUserCanCreateStatus" class="mt-5 mr-3 mb-2">
    <ChecklistStatusDialogNewStatus :category="category.name" />
  </div>

  <ChecklistStatusDialogDeleteConfirmation v-if="statusToDelete"
                                    :status="statusToDelete"
                                    @confirm="onConfirmStatusDeletion"
                                    @close="onCancelStatusDeletion"
  />
</div>
</template>

<script>
import Draggable from 'vuedraggable'
import { mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import ChecklistStatusDialogDeleteConfirmation from '@/project/checklist/dialogs/ChecklistStatusDialogDeleteConfirmation.vue'
import ChecklistStatusDialogEditMenu from '@/project/checklist/dialogs/ChecklistStatusDialogEditMenu.vue'
import ChecklistStatusDialogNewStatus from '@/project/checklist/dialogs/ChecklistStatusDialogNewStatus.vue'
import { statusColors } from '@/project/checklist/dialogs/taskStatus'

const STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_EDITION = 18
const STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_READONLY = 33

export default {
  name: 'ChecklistStatusDialogCategory',
  components: {
    Draggable,
    AppTooltip,
    ChecklistStatusDialogDeleteConfirmation,
    ChecklistStatusDialogEditMenu,
    ChecklistStatusDialogNewStatus,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
    isBulkEdition: {
      type: Boolean,
    },
    selectedStatusId: {
      type: Number,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      statusToDelete: null,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    statusCategoryColor () {
      return statusColors[this.category.name]
    },
    currentUserCanCreateStatus () {
      return this.isCurrentUserPm && !this.isBulkEdition
    },
    statusLabelMaxDisplayedLength () {
      return this.editMode
        ? STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_EDITION
        : STATUS_LABEL_MAX_DISPLAYED_LENGTH_IN_READONLY
    },
  },
  methods: {
    onStatusClick (statusId) {
      this.$emit('update:selectedStatusId', statusId)
    },
    onStatusEdit (editedStatus) {
      this.$emit('status-edit', { editedCategory: this.category.name, editedStatus })
    },
    onStatusDelete (statusToDelete) {
      this.statusToDelete = statusToDelete
    },
    onConfirmStatusDeletion () {
      this.$emit('status-delete', { status: this.statusToDelete, category: this.category.name })
      this.statusToDelete = null
    },
    onCancelStatusDeletion () {
      this.statusToDelete = null
    },
  },
}
</script>

<style scoped lang="scss">
.ChecklistStatusDialogCategory {
  display: flex;
  flex-direction: column;
  width: 280px;
  // padding: 0 0 12px 12px;
  border: 1px solid rgb(var(--v-theme-border-primary));
  border-radius: 6px;

  &__titleContainer {
    padding: 8px 0 8px 12px;
    border-bottom: 1px solid rgb(var(--v-theme-border-primary));
  }

  &__statusContainer {
    display: flex;
    overflow-x: hidden;
    flex: 1;
    padding: 12px;
  }
  &__statusFlexContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;

    &:last-child {
      margin-bottom: 80px;
    }
  }
  &__draggableArea {
    overflow-y: auto;
    width: 100%;
  }
  &__grabIcon {
    font-size: 16px;
    color: rgb(var(--v-theme-greyDark-lighten-1));
    cursor: grab;
  }
  &__status {
    height: auto;
    min-height: 24px;
    text-align: center;
    border: 2px solid transparent;

    &--active {
      border-width: 4px;
      border-color: rgb(var(--v-theme-tertiary)) !important;
    }

    :deep(.v-chip__content) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: inline-block;
    }
  }
  &__edition-buttons {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}
.status-chip-tick-wrapper {
  min-width: 0;
  &.selectable:hover {
    cursor: pointer;
    background-color: rgba(var(--v-theme-grey-900), 0.05);
  }
}
</style>
